import { FloatingLabel, Form, Spinner } from "react-bootstrap";
import { useFormik } from "formik";
import {
  showToastError,
  showToastSuccess,
} from "components/toastify/ToastNotifications";
import { AiOutlineClose } from "react-icons/ai";

import { useEffect, useState } from "react";
import {
  useCreateCurrencyRuleMutation,
  useGetBusinessLinesQuery,
  useGetSupportedCurrenciesQuery,
} from "api/ruleEngineApi";
import { addCurrencyRuleSchema } from "validation-schema/rule-engine-schema";

type Props = {
  activeModalTab: string;
  onClose?: () => void;
  onSuccess?: () => void;
};

const statusOptions = [
  { name: "Active" },
  { name: "Inactive" },
  { name: "Pending" },
];

const AddCurrencyRuleModal = ({
  activeModalTab,
  onClose,
  onSuccess,
}: Props) => {
  const [createCurrencyRule, { isLoading: isCreating }] =
    useCreateCurrencyRuleMutation();

  const {
    data: businessLinesData,
    error: businessLinesError,
    isFetching: isFetchingBusinessLinesBusinessLines,
  } = useGetBusinessLinesQuery();

  const {
    data: supportedCurrenciesData,
    error: currenciesError,
    isFetching: isFetchingCurrencies,
  } = useGetSupportedCurrenciesQuery();

  const [businessLines, setBusinessLines] = useState<string[]>([]);
  const [supportedCurrencies, setSupportedCurrencies] = useState<string[]>([]);

  const revenueLineMapping: Record<string, number> = {
    SAAS: 1,
    "Phone Number Purchase": 2,
    "Phone Number Credit": 3,
    "Booking Fee": 4,
    All: 5,
    "VAT" : 6,
  };

  const formik = useFormik({
    initialValues: {
      revenueLine: "SAAS",
      costCurrency: "EUR",
      effectiveDate: "",
      externalSupplier: false,
      sellCurrency: "EUR",
      status: "Active",
      sellRate: "1",
      baseCostAmount: "2",
      externalSupplierCost: "",
    },
    validationSchema: addCurrencyRuleSchema,
    onSubmit: async (values) => {
      
      try {
        const currencyRuleData = {
          application: "Joble",
          businessLine: revenueLineMapping[values.revenueLine] || 0, // 👈 mapped number
          sellCurrency: values.sellCurrency,
          costCurrency: values.costCurrency,
          sellRate: parseFloat(values.sellRate) || 0,
          costAmount: parseFloat(values.baseCostAmount) || 0,
          externalSupplier: values.externalSupplier ? "External" : "Internal",
          externalSupplierCost: parseFloat(values.externalSupplierCost) || 0,
          effectiveDate: new Date(values.effectiveDate).toISOString(),
        };

        

        const result = await createCurrencyRule(currencyRuleData).unwrap();

        

        showToastSuccess("Currency rule created successfully!");

        if (onSuccess) {
          onSuccess();
        } else if (onClose) {
          onClose();
        }
      } catch (error: any) {
        console.error("Error creating currency rule:", error);
        
        showToastError(
          error?.data?.ResponseMessage ||
            error?.data?.message ||
            "An error occurred while creating the currency rule"
        );
      }
    },
  });

  const incrementValue = (fieldName: string) => {
    const currentValue =
      parseFloat(
        formik.values[fieldName as keyof typeof formik.values] as string
      ) || 0;
    formik.setFieldValue(fieldName, (currentValue + 1).toString());
  };

  const decrementValue = (fieldName: string) => {
    const currentValue =
      parseFloat(
        formik.values[fieldName as keyof typeof formik.values] as string
      ) || 0;
    formik.setFieldValue(fieldName, Math.max(0, currentValue - 1).toString());
  };

  useEffect(() => {
    if (businessLinesData) {
      const response = businessLinesData;
      if (response.data?.items) {
        
        setBusinessLines(response.data.items);
      }
    }
    if (businessLinesError) {
      console.error("Error fetching business lines:", businessLinesError);
      showToastError("Failed to load business lines");
    }
  }, [businessLinesData, businessLinesError]);

  //my effect
  useEffect(() => {
    if (businessLinesData) {
       // <-- check here
    }
  }, [businessLinesData]);

  // Process supported currencies data
  useEffect(() => {
    if (supportedCurrenciesData) {
      const response = supportedCurrenciesData;
      if (response.data?.items) {
        setSupportedCurrencies(response.data.items);
      }
    }
    if (currenciesError) {
      console.error("Error fetching supported currencies:", currenciesError);
      showToastError("Failed to load supported currencies");
    }
  }, [supportedCurrenciesData, currenciesError]);

  return (
    <div>
      {/* Header */}
      <div
        className="modal-header"
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          padding: "45px 24px 45px 24px",
          borderBottom: "none",
        }}
      >
        <h5
          style={{
            margin: 0,
            fontSize: "24px",
            fontWeight: "600",
            color: "#1f2937",
          }}
        >
          Add Currency Rule
        </h5>
        <button
          onClick={onClose}
          style={{
            background: "none",
            border: "none",
            fontSize: "20px",
            cursor: "pointer",
            fontWeight: "600",
            color: "#6b7280",
          }}
        >
          <AiOutlineClose />
        </button>
      </div>

      {/* Form Content */}
      <div style={{ padding: "0 24px 24px 24px" }}>
        <Form onSubmit={formik.handleSubmit}>
          {/* Row 1 */}
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              gap: "16px",
              marginBottom: "20px",
            }}
          >
            <div style={{ flex: 1, maxWidth: "246px" }}>
              <FloatingLabel
                controlId="floatingRevenueLine"
                label="Revenue Line"
                className="float-input"
              >
                <Form.Select
                  name="revenueLine"
                  value={formik.values.revenueLine}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  style={{
                    borderRadius: "8px",
                    fontSize: "14px",
                    height: "58px",
                    maxWidth: "246px",
                  }}
                  aria-label="Floating label select example"
                  className="border-0 w-100"
                >
                  <option value="">Select Revenue Line</option>
                  {businessLines?.map((line) => (
                    <option key={line} value={line}>
                      {line}
                    </option>
                  ))}
                </Form.Select>
              </FloatingLabel>
              {formik.touched.revenueLine && formik.errors.revenueLine && (
                <span style={{ color: "#ef4444", fontSize: "12px" }}>
                  {formik.errors.revenueLine}
                </span>
              )}
            </div>

            <div style={{ flex: 1, maxWidth: "246px" }}>
              <FloatingLabel
                controlId="floatingSellCurrency"
                label="Sell Currency"
                className="float-input"
              >
                <Form.Select
                  name="sellCurrency"
                  value={formik.values.sellCurrency}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  style={{
                    borderRadius: "8px",
                    fontSize: "14px",
                    height: "58px",
                    maxWidth: "246px",
                  }}
                  aria-label="Floating label select example"
                  className="border-0 w-100"
                >
                  <option value="">Select Currency</option>
                  {supportedCurrencies?.map((currency) => (
                    <option key={currency} value={currency}>
                      {currency}
                    </option>
                  ))}
                </Form.Select>
              </FloatingLabel>
              {formik.touched.sellCurrency && formik.errors.sellCurrency && (
                <span style={{ color: "#ef4444", fontSize: "12px" }}>
                  {formik.errors.sellCurrency}
                </span>
              )}
            </div>
          </div>

          {/* Row 2 */}
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              gap: "16px",
              marginBottom: "20px",
            }}
          >
            <div style={{ flex: 1, maxWidth: "246px" }}>
              <FloatingLabel
                controlId="floatingCostCurrency"
                label="Cost Currency"
                className="float-input"
              >
                <Form.Select
                  name="costCurrency"
                  value={formik.values.costCurrency}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  style={{
                    borderRadius: "8px",
                    fontSize: "14px",
                    height: "58px",
                    maxWidth: "246px",
                  }}
                  aria-label="Floating label select example"
                  className="border-0 w-100"
                >
                  <option value="">Select Currency</option>
                  {supportedCurrencies?.map((currency) => (
                    <option key={currency} value={currency}>
                      {currency}
                    </option>
                  ))}
                </Form.Select>
              </FloatingLabel>
              {formik.touched.costCurrency && formik.errors.costCurrency && (
                <span style={{ color: "#ef4444", fontSize: "12px" }}>
                  {formik.errors.costCurrency}
                </span>
              )}
            </div>

            <div style={{ flex: 1, maxWidth: "246px" }}>
              <FloatingLabel
                controlId="floatingEffectiveDate"
                label="Effective Date"
                className="float-input"
              >
                <Form.Control
                  type="date"
                  name="effectiveDate"
                  value={formik.values.effectiveDate}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  style={{
                    borderRadius: "8px",
                    fontSize: "14px",
                    height: "58px",
                    maxWidth: "246px",
                  }}
                  aria-label="Floating label select example"
                  className="border-0 w-100"
                />
              </FloatingLabel>
              {formik.touched.effectiveDate && formik.errors.effectiveDate && (
                <span style={{ color: "#ef4444", fontSize: "12px" }}>
                  {formik.errors.effectiveDate}
                </span>
              )}
            </div>
          </div>

          {/* Row 3 - External Supplier and Sell Rate */}
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              gap: "16px",
              marginBottom: "20px",
            }}
          >
            <div style={{ flex: 1, maxWidth: "246px" }}>
              <div
                style={{
                  borderRadius: "8px",
                  padding: "0 16px",
                  height: "58px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  backgroundColor: "white",
                  maxWidth: "246px",
                }}
              >
                <span
                  style={{
                    fontSize: "14px",
                    color: "#374151",
                    fontWeight: "400",
                  }}
                >
                  External Supplier
                </span>
                <label
                  style={{
                    position: "relative",
                    display: "inline-block",
                    width: "44px",
                    height: "24px",
                  }}
                >
                  <input
                    type="checkbox"
                    name="externalSupplier"
                    checked={formik.values.externalSupplier}
                    onChange={formik.handleChange}
                    style={{ opacity: 0, width: 0, height: 0 }}
                  />
                  <span
                    style={{
                      position: "absolute",
                      cursor: "pointer",
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      backgroundColor: formik.values.externalSupplier
                        ? "#3b82f6"
                        : "#d1d5db",
                      borderRadius: "12px",
                      transition: "0.3s ease-in-out",
                    }}
                  >
                    <span
                      style={{
                        position: "absolute",
                        content: "",
                        height: "18px",
                        width: "18px",
                        left: formik.values.externalSupplier ? "23px" : "3px",
                        bottom: "3px",
                        backgroundColor: "white",
                        borderRadius: "50%",
                        transition: "0.3s ease-in-out",
                        boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                      }}
                    />
                  </span>
                </label>
              </div>
            </div>

            {!formik.values.externalSupplier && (
              <div className="rate" style={{ flex: 1, maxWidth: "246px" }}>
                <div
                  style={{
                    position: "relative",
                    width: "100%",
                    maxWidth: "246px",
                  }}
                >
                  <label
                    style={{
                      position: "absolute",
                      top: "8px",
                      left: "16px",
                      fontSize: "12px",
                      color: "#6b7280",
                      fontWeight: "400",
                      zIndex: 1,
                      backgroundColor: "white",
                      padding: "0 4px",
                    }}
                  >
                    {`Sell Rate (vs ${formik.values.sellCurrency || "..."})`}
                  </label>
                  <input
                    type="number"
                    name="sellRate"
                    value={formik.values.sellRate}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    style={{
                      width: "100%",
                      height: "58px",
                      border: "none",
                      // border: "1px solid #e5e7eb",
                      borderRadius: "8px",
                      fontSize: "24px",
                      fontWeight: "600",
                      color: "#3b82f6",
                      padding: "27px 60px 8px 16px",
                      backgroundColor: "white",
                      outline: "none",
                      maxWidth: "246px",
                    }}
                  />
                  <div
                    style={{
                      position: "absolute",
                      right: "12px",
                      top: "50%",
                      transform: "translateY(-50%)",
                      display: "flex",
                      flexDirection: "column",
                      gap: "2px",
                    }}
                  >
                    <button
                      type="button"
                      onClick={() => incrementValue("sellRate")}
                      style={{
                        width: "32px",
                        height: "20px",
                        backgroundColor: "#f3f4f6",
                        border: "1px solid #d1d5db",
                        borderRadius: "4px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        cursor: "pointer",
                        fontSize: "10px",
                        color: "#6b7280",
                        transition: "all 0.2s ease",
                      }}
                      onMouseOver={(e) => {
                        e.currentTarget.style.backgroundColor = "#e5e7eb";
                      }}
                      onMouseOut={(e) => {
                        e.currentTarget.style.backgroundColor = "#f3f4f6";
                      }}
                    >
                      ▲
                    </button>
                    <button
                      type="button"
                      onClick={() => decrementValue("sellRate")}
                      style={{
                        width: "32px",
                        height: "20px",
                        backgroundColor: "#f3f4f6",
                        border: "1px solid #d1d5db",
                        borderRadius: "4px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        cursor: "pointer",
                        fontSize: "10px",
                        color: "#6b7280",
                        transition: "all 0.2s ease",
                      }}
                      onMouseOver={(e) => {
                        e.currentTarget.style.backgroundColor = "#e5e7eb";
                      }}
                      onMouseOut={(e) => {
                        e.currentTarget.style.backgroundColor = "#f3f4f6";
                      }}
                    >
                      ▼
                    </button>
                  </div>
                </div>
                {formik.touched.sellRate && formik.errors.sellRate && (
                  <span style={{ color: "#ef4444", fontSize: "12px" }}>
                    {formik.errors.sellRate}
                  </span>
                )}
              </div>
            )}
          </div>

          {/* Row 4 - Base Cost Amount */}
          {!formik.values.externalSupplier && (
            <div
              style={{
                display: "flex",
                justifyContent: "flex-end",
                gap: "16px",
                marginBottom: "20px",
              }}
            >
              <div style={{ flex: 1, maxWidth: "246px" }}>
                <div
                  style={{
                    position: "relative",
                    width: "100%",
                    maxWidth: "246px",
                  }}
                >
                  <label
                    style={{
                      position: "absolute",
                      top: "8px",
                      left: "16px",
                      fontSize: "12px",
                      color: "#6b7280",
                      fontWeight: "400",
                      zIndex: 1,
                      backgroundColor: "white",
                      padding: "0 4px",
                    }}
                  >
                    Base Cost Amount
                  </label>
                  <input
                    type="number"
                    name="baseCostAmount"
                    value={formik.values.baseCostAmount}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    style={{
                      width: "100%",
                      height: "58px",
                      border: "none",
                      // border: "1px solid #e5e7eb",
                      borderRadius: "8px",
                      fontSize: "24px",
                      fontWeight: "600",
                      color: "#3b82f6",
                      padding: "27px 60px 8px 16px",
                      backgroundColor: "white",
                      outline: "none",
                      maxWidth: "246px",
                    }}
                  />
                  <div
                    style={{
                      position: "absolute",
                      right: "12px",
                      top: "50%",
                      transform: "translateY(-50%)",
                      display: "flex",
                      flexDirection: "column",
                      gap: "2px",
                    }}
                  >
                    <button
                      type="button"
                      onClick={() => incrementValue("baseCostAmount")}
                      style={{
                        width: "32px",
                        height: "20px",
                        backgroundColor: "#f3f4f6",
                        border: "1px solid #d1d5db",
                        borderRadius: "4px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        cursor: "pointer",
                        fontSize: "10px",
                        color: "#6b7280",
                        transition: "all 0.2s ease",
                      }}
                      onMouseOver={(e) => {
                        e.currentTarget.style.backgroundColor = "#e5e7eb";
                      }}
                      onMouseOut={(e) => {
                        e.currentTarget.style.backgroundColor = "#f3f4f6";
                      }}
                    >
                      ▲
                    </button>
                    <button
                      type="button"
                      onClick={() => decrementValue("baseCostAmount")}
                      style={{
                        width: "32px",
                        height: "20px",
                        backgroundColor: "#f3f4f6",
                        border: "1px solid #d1d5db",
                        borderRadius: "4px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        cursor: "pointer",
                        fontSize: "10px",
                        color: "#6b7280",
                        transition: "all 0.2s ease",
                      }}
                      onMouseOver={(e) => {
                        e.currentTarget.style.backgroundColor = "#e5e7eb";
                      }}
                      onMouseOut={(e) => {
                        e.currentTarget.style.backgroundColor = "#f3f4f6";
                      }}
                    >
                      ▼
                    </button>
                  </div>
                </div>
                {formik.touched.baseCostAmount &&
                  formik.errors.baseCostAmount && (
                    <span style={{ color: "#ef4444", fontSize: "12px" }}>
                      {formik.errors.baseCostAmount}
                    </span>
                  )}
              </div>
            </div>
          )}

          {/* External Supplier Cost Section - Conditional with Smooth Transition */}
          <div
            style={{
              maxHeight: formik.values.externalSupplier ? "200px" : "0",
              overflow: "hidden",
              transition:
                "max-height 0.3s ease-in-out, opacity 0.3s ease-in-out",
              opacity: formik.values.externalSupplier ? 1 : 0,
              marginBottom: formik.values.externalSupplier ? "20px" : "0",
            }}
          >
            <div
              style={{
                backgroundColor: "rgba(242, 153, 74, 0.2)",
                borderRadius: "8px",
                padding: "16px",
              }}
            >
              <p
                style={{
                  fontSize: "16px !important",
                  color: "#AE6900",
                  margin: "0 0 17px 0",
                }}
              >
                % (for margin calculation)
              </p>
              <div style={{ position: "relative" }}>
                <Form.Control
                  type="number"
                  name="externalSupplierCost"
                  value={formik.values.externalSupplierCost}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  placeholder="e.g., 0.0808 per AI minute"
                  style={{
                    borderRadius: "6px",
                    fontSize: "14px",
                    border: "1px solid #f59e0b",
                    backgroundColor: "inherit",
                    paddingRight: "40px",
                    height: "50px",
                  }}
                />
                <div
                  style={{
                    position: "absolute",
                    right: "8px",
                    top: "50%",
                    transform: "translateY(-50%)",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                  }}
                >
                  <button
                    type="button"
                    onClick={() => {
                      const current =
                        parseFloat(formik.values.externalSupplierCost) || 0;
                      formik.setFieldValue(
                        "externalSupplierCost",
                        (current + 0.0001).toFixed(4)
                      );
                    }}
                    style={{
                      background: "none",
                      border: "none",
                      fontSize: "10px",
                      cursor: "pointer",
                      color: "#92400e",
                      padding: "2px 4px",
                      lineHeight: "1",
                    }}
                  >
                    ▲
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      const current =
                        parseFloat(formik.values.externalSupplierCost) || 0;
                      formik.setFieldValue(
                        "externalSupplierCost",
                        Math.max(0, current - 0.0001).toFixed(4)
                      );
                    }}
                    style={{
                      background: "none",
                      border: "none",
                      fontSize: "10px",
                      cursor: "pointer",
                      color: "#92400e",
                      padding: "2px 4px",
                      lineHeight: "1",
                    }}
                  >
                    ▼
                  </button>
                </div>
              </div>
              <p
                style={{
                  fontSize: "12px",
                  color: "#AE6900",
                  marginTop: "8px",
                  margin: "8px 0 0 0",
                }}
              >
                This cost will be used for accurate margin calculations in
                financial reports.
              </p>
            </div>
          </div>

          {/* Save Button */}
          <button
            disabled={isCreating}
            type="submit"
            style={{
              width: "100%",
              backgroundColor: "#0ea5e9",
              color: "white",
              border: "none",
              borderRadius: "24px",
              padding: "14px 20px",
              fontSize: "14px",
              fontWeight: "600",
              cursor: isCreating ? "not-allowed" : "pointer",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: "8px",
              opacity: isCreating ? 0.7 : 1,
              transition: "all 0.2s ease-in-out",
              borderTop: "",
            }}
            onMouseOver={(e) => {
              if (!isCreating) {
                e.currentTarget.style.backgroundColor = "#0284c7";
              }
            }}
            onMouseOut={(e) => {
              if (!isCreating) {
                e.currentTarget.style.backgroundColor = "#0ea5e9";
              }
            }}
          >
            Save {isCreating && <Spinner size="sm" />}
          </button>
        </Form>
      </div>
    </div>
  );
};

export default AddCurrencyRuleModal;

